import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { UserProfile, UserPreferences, UserStats } from '@coffeeplexity/shared';
import {
  userApi,
  UpdateProfileData,
  UpdatePreferencesData,
} from '../../services/api/userApi';

interface UserState {
  profile: UserProfile | null;
  preferences: UserPreferences | null;
  stats: UserStats | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
}

const initialState: UserState = {
  profile: null,
  preferences: null,
  stats: null,
  isLoading: false,
  isUpdating: false,
  error: null,
};

// Async thunks
export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userApi.getProfile(userId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch profile'
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async (data: UpdateProfileData, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { user: { id: string } } };
      const userId = state.auth.user?.id;

      if (!userId) {
        throw new Error('User ID not found');
      }

      const result = await userApi.updateProfile(userId, data);
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          'Failed to update profile'
      );
    }
  }
);

export const fetchUserPreferences = createAsyncThunk(
  'user/fetchPreferences',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userApi.getPreferences(userId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch preferences'
      );
    }
  }
);

export const updateUserPreferences = createAsyncThunk(
  'user/updatePreferences',
  async (
    { userId, data }: { userId: string; data: UpdatePreferencesData },
    { rejectWithValue }
  ) => {
    try {
      return await userApi.updatePreferences(userId, data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update preferences'
      );
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  'user/fetchStats',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userApi.getStats(userId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch stats'
      );
    }
  }
);

export const uploadUserAvatar = createAsyncThunk(
  'user/uploadAvatar',
  async (
    { userId, imageUri }: { userId: string; imageUri: string },
    { rejectWithValue }
  ) => {
    try {
      return await userApi.uploadAvatar(userId, imageUri);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to upload avatar'
      );
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearUserData: state => {
      state.profile = null;
      state.preferences = null;
      state.stats = null;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Profile
      .addCase(fetchUserProfile.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Profile
      .addCase(updateUserProfile.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        // FIXED: Replace entire profile with response, don't merge
        // Backend returns complete updated profile
        state.profile = action.payload as any;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Fetch Preferences
      .addCase(fetchUserPreferences.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = action.payload;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Preferences
      .addCase(updateUserPreferences.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.preferences = action.payload;
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Fetch Stats
      .addCase(fetchUserStats.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Upload Avatar
      .addCase(uploadUserAvatar.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(uploadUserAvatar.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Update avatar URL in profile if it exists
        if (state.profile) {
          state.profile = {
            ...state.profile,
            avatar_url: action.payload.avatar_url,
          };
        }
      })
      .addCase(uploadUserAvatar.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearUserData } = userSlice.actions;

// Selectors
export const selectUserProfile = (state: { user: UserState }) =>
  state.user.profile;
export const selectUserPreferences = (state: { user: UserState }) =>
  state.user.preferences;
export const selectUserStats = (state: { user: UserState }) => state.user.stats;
export const selectUserLoading = (state: { user: UserState }) =>
  state.user.isLoading;
export const selectUserUpdating = (state: { user: UserState }) =>
  state.user.isUpdating;
export const selectUserError = (state: { user: UserState }) => state.user.error;

export default userSlice.reducer;
